// Agreed product rule (confirmed with the user): the optimizer only handles
// linked orders of up to 2 stops. Groups with more than 2 stops are deliberately
// excluded from optimization and moved to the Unfit Stops bucket for manual
// sequencing — this is intended behaviour, not a limitation to "fix". Dispatchers
// drag those stops onto a vehicle by hand and then calculate the time.
export const OPTIMIZE_MULTISTOP_MSG =
  'Linked orders with more than 2 stops were moved to Unfit Stops and excluded from optimization.'

export const UNFIT_STOPS_VEHICLE = 'Unfit Stops'

// Unassigned buckets the optimize backend draws orders from (it reprocesses them
// into vehicles / its unfit result), so multi-stops sitting here must also be
// kept out of the payload — regardless of the vehicle filter.
export const UNASSIGNED_BUCKETS = new Set(['Unfit Stops', 'ReadyToRoute'])

function refOf(s: any): string {
  if (!s || s.orderType === 'HUB') return ''
  const ref = s.linkedRef
  return typeof ref === 'string' && ref ? ref : ''
}

/** Group refs shared by more than 2 stops (HUB/blank refs excluded).
 *  The `> 2` threshold is the agreed cap on optimizer-supported linked orders
 *  (see OPTIMIZE_MULTISTOP_MSG) — anything past it is quarantined to Unfit. */
export function oversizedGroupRefs(stops: any[]): Set<string> {
  const counts = new Map<string, number>()
  for (const s of stops || []) {
    const ref = refOf(s)
    if (!ref) continue
    counts.set(ref, (counts.get(ref) || 0) + 1)
  }
  const out = new Set<string>()
  counts.forEach((n, ref) => { if (n > 2) out.add(ref) })
  return out
}

/** True when any linked group in `stops` exceeds 2 stops. */
export function hasOversizedLinkedGroup(stops: any[]): boolean {
  return oversizedGroupRefs(stops).size > 0
}

/** Oversized group refs within the optimize scope: unlocked, non-PreRoute routes
 *  narrowed by `selectedVehicles` (empty = all). */
export function oversizedGroupsInOptimizeSet(routes: any[], selectedVehicles: string[]): Set<string> {
  const vehicleFilter =
    selectedVehicles && selectedVehicles.length ? new Set(selectedVehicles) : null
  const stops: any[] = []
  for (const r of routes || []) {
    if (!r || r.isTruckLocked) continue
    const vehicle = r.vehicle
    if (typeof vehicle === 'string' && vehicle.includes('PreRoute')) continue
    // Skip out-of-filter assigned vehicles, but always scan the unassigned
    // buckets — the backend reprocesses them, so their multi-stops must be caught.
    if (vehicleFilter && !vehicleFilter.has(vehicle) && !UNASSIGNED_BUCKETS.has(vehicle)) continue
    for (const s of r.stops || []) stops.push(s)
  }
  return oversizedGroupRefs(stops)
}

/** All stop ids in the same linked group as any seed. Primary key is `linkedRef`;
 *  a seed with no ref but a `linkedOrder` falls back to the self+partner pair
 *  (old-schema pairs). Seeds are always included. */
export function expandToLinkedGroupIds(routes: any[], seedIds: string[]): Set<string> {
  const seeds = new Set(seedIds || [])
  const seedRefs = new Set<string>()
  const out = new Set<string>(seeds)
  for (const r of routes || []) {
    for (const s of r?.stops || []) {
      if (s && seeds.has(s.id)) {
        const ref = refOf(s)
        if (ref) seedRefs.add(ref)
        else if (s.linkedOrder) out.add(s.linkedOrder) // old-schema pair fallback
      }
    }
  }
  if (seedRefs.size) {
    for (const r of routes || []) {
      for (const s of r?.stops || []) {
        if (s && refOf(s) && seedRefs.has(s.linkedRef)) out.add(s.id)
      }
    }
  }
  return out
}

/** Remove every stop whose id is in `groupIds` from all routes except the target
 *  vehicle's route, set their vehicle, and return them (source order preserved).
 *  Mutates routes in place. HUB stops never move. */
export function collectGroupStops(
  routes: any[],
  groupIds: Set<string> | string[],
  targetVehicle: string,
): any[] {
  const ids = groupIds instanceof Set ? groupIds : new Set(groupIds || [])
  const moved: any[] = []
  for (const r of routes || []) {
    if (!r || r.vehicle === targetVehicle) continue
    const stops = r.stops || []
    const kept: any[] = []
    const taken: any[] = []
    for (const s of stops) {
      if (s && s.orderType !== 'HUB' && ids.has(s.id)) {
        s.vehicle = targetVehicle
        taken.push(s)
      } else {
        kept.push(s)
      }
    }
    if (taken.length) {
      r.stops = kept
      moved.push(...taken)
    }
  }
  return moved
}

export interface OptimizePayloadSplit {
  payloadRoutes: any[]
  excludedStops: any[]
}

/**
 * Split routes for an optimize request: pull out every stop in an oversized (>2)
 * linked group (within the optimize scope) so they are NOT sent to the backend,
 * and return them as `excludedStops` (vehicle set to Unfit Stops, `linkedRef`
 * kept) to be appended to the Unfit Stops route once the optimized result is
 * applied. `payloadRoutes` is a shallow clone with those stops removed; the input
 * is not mutated.
 */
export function splitOptimizePayload(
  routes: any[],
  selectedVehicles: string[],
  unfitVehicle: string = UNFIT_STOPS_VEHICLE,
): OptimizePayloadSplit {
  const refs = oversizedGroupsInOptimizeSet(routes, selectedVehicles)
  const excludedStops: any[] = []
  const payloadRoutes = (routes || []).map((r) => {
    if (!r) return r
    if (!refs.size) return { ...r, stops: [...(r.stops || [])] }
    const kept: any[] = []
    for (const s of r.stops || []) {
      if (s && s.orderType !== 'HUB' && refOf(s) && refs.has(s.linkedRef)) {
        excludedStops.push({ ...s, vehicle: unfitVehicle })
      } else {
        kept.push(s)
      }
    }
    return { ...r, stops: kept }
  })
  return { payloadRoutes, excludedStops }
}

/**
 * Merge the held-back multi-stop groups into an Unfit Stops `stops` array:
 * de-dupe by id (a group already in Unfit was stripped from the payload but is
 * still present here) and insert them BETWEEN the start and end hub stops rather
 * than after the trailing hub. Returns a new array.
 */
export function mergeExcludedIntoUnfit(unfitStops: any[], excludedStops: any[]): any[] {
  const excluded = excludedStops || []
  const excludedIds = new Set(excluded.map((s) => s && s.id))
  const base = (unfitStops || []).filter((s) => !s || !excludedIds.has(s.id))
  if (!excluded.length) return base
  const last = base[base.length - 1]
  const lastIsHub =
    last && (last.orderType === 'HUB' || (typeof last.id === 'string' && last.id.startsWith('END')))
  if (lastIsHub) return [...base.slice(0, base.length - 1), ...excluded, last]
  return [...base, ...excluded]
}

/** Convert routes to the old paired format the optimize backend expects: drop
 *  `linkedRef` from every stop (the single `linkedOrder` getRoutes provides is
 *  the format the backend reads). Returns a shallow clone; does not mutate. */
export function toBackendRoutes(routes: any[]): any[] {
  return (routes || []).map((r) => {
    if (!r) return r
    const stops = (r.stops || []).map((s: any) => {
      if (!s || !('linkedRef' in s)) return s
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { linkedRef, ...rest } = s
      return rest
    })
    return { ...r, stops }
  })
}
